// src/pages/EventRegistrationPage.js
// Multi-step event registration: Form -> CPD (conditional) -> Review -> Payment/Confirmation
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const GOVERNING_BODIES = [
  'Allied Health Professions Council',
  'Ghana Medical Association',
  'Ghana Registered Nurses and Midwives Association',
  'Ghana Pharmacy Council',
  'Medical and Dental Council',
  'Nursing and Midwifery Council',
  'Health Professions Council of South Africa',
  'Allied Health Professions Council of Zambia',
  'Clinical Officers Council',
  'Physiotherapy Council',
  'Pharmacy and Poisons Board',
  'Medical Council'
];

const PROFESSIONS = [
  'Respiratory Therapist', 'Physiotherapist', 'Physiotherapy Technician', 'Physiotherapy Assistant',
  'Nurse', 'Midwife', 'Pharmacist', 'Medical Doctor', 'Clinical Officer',
  'Occupational Therapist', 'Speech and Language Therapist', 'Radiographer', 'Sonographer',
  'Medical Laboratory Scientist', 'Laboratory Scientist', 'Biomedical Scientist',
  'Medical Physicist', 'Nutritionist', 'Dietitian', 'Disease Control Officer',
  'Dentist', 'Dental Therapist', 'Dental Hygienist', 'EMT', 'Paramedic',
  'Audiologist', 'Optometrist', 'Public Health Professional', 'Health Promotion Officer',
  'Community Health Worker', 'Health Information Officer', 'Medical Record Assistant',
  'Health Administrator', 'Other Allied Health Profession', 'Other'
];

const BRAND = '#6B8E23';

const EventRegistrationPage = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Details, 2: CPD, 3: Review, 4: Confirmation
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    organization: '',
    jobTitle: '',
    requiresCpd: '', // 'yes' | 'no'
    cpdPoints: '',
    governingBody: '',
    governingBodyOther: '',
    pinNumber: '',
    areaOfStudy: '',
    profession: '',
    professionOther: '',
    crceSelected: false,
    cpdAfricaSelected: false,
    certificateSelected: false,
    isArCaMember: false,
    membershipIdNumber: '',
    professionalPin: '',
    cpdAlreadyPaid: false,
    cpdMomoTransactionId: ''
  });

  // Registration is open to everyone unless the admin marked the event Members Only.
  // If the user is logged in, their token links the registration to their member account.
  useEffect(() => {
    const token = localStorage.getItem('userToken') || localStorage.getItem('token');
    if (event && (event.members_only === 1 || event.members_only === true) && !user && !token) {
      alert('This event is for ARCA members only. Please login to register.');
      navigate('/login');
    }
  }, [event, user, navigate]);

  // Prefill from logged-in user profile
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        firstName: prev.firstName || user.firstName || user.first_name || '',
        lastName: prev.lastName || user.lastName || user.last_name || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || '',
        country: prev.country || user.country || '',
        organization: prev.organization || user.institution || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/events/${eventId}`);
        const data = await response.json();
        if (data.success) {
          setEvent(data.data.event);
        } else {
          setError(data.message || 'Event not found');
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event details');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  // Parse admin-configured CPD tiers
  const cpdTiers = React.useMemo(() => {
    if (!event || !event.cpd_tiers) return [];
    try {
      const tiers = typeof event.cpd_tiers === 'string' ? JSON.parse(event.cpd_tiers) : event.cpd_tiers;
      return Array.isArray(tiers) ? tiers : [];
    } catch (e) {
      return [];
    }
  }, [event]);

  // Detect webinar-series events (October or November)
  const isOctoberEvent = React.useMemo(() => {
    if (!event || !event.start_date) return false;
    const month = new Date(event.start_date).getMonth();
    return month === 9 || month === 10; // October or November
  }, [event]);

  const wantsCrce = form.crceSelected;
  const wantsCpdAfrica = form.cpdAfricaSelected;
  const wantsCertificate = form.certificateSelected;
  const claimsArCaMember = form.isArCaMember;

  const wantsCpd = form.requiresCpd === 'yes';
  const selectedTier = cpdTiers.find(t => String(t.points) === String(form.cpdPoints));
  const cpdFee = wantsCpd && selectedTier ? parseFloat(selectedTier.price) || 0 : 0;

  // Webinar-series event fixed pricing
  const isCpdAfricaMember = !!user || (claimsArCaMember && !!form.membershipIdNumber && !!form.professionalPin);
  const crceFee = isOctoberEvent && wantsCrce ? 10 : 0;
  const cpdAfricaAlreadyPaid = isOctoberEvent && wantsCpdAfrica && form.cpdAlreadyPaid;
  const cpdAfricaFee = isOctoberEvent && wantsCpdAfrica && !cpdAfricaAlreadyPaid ? (isCpdAfricaMember ? 150 : 200) : 0;
  const cpdAfricaCurrency = isOctoberEvent && wantsCpdAfrica ? 'GHC' : null;
  const certificateFee = isOctoberEvent && wantsCertificate
    ? (wantsCrce || wantsCpdAfrica ? 0 : 10)
    : 0;

  // Compute event fee using same logic as existing EventDetailsPage
  const computeEventFee = () => {
    if (!event || event.category !== 'Paid') return { fee: 0, priceType: 'Free' };
    let selectedPrice = event.non_member_price || event.member_price || event.early_bird_price || event.student_price;
    let priceType = 'Non-Member';
    if (user) {
      if (user.membershipType === 'Student' && event.student_price) {
        selectedPrice = event.student_price;
        priceType = 'Student';
      } else if (event.member_price) {
        selectedPrice = event.member_price;
        priceType = 'Member';
      }
      if (event.early_bird_price && event.early_bird_deadline && new Date() < new Date(event.early_bird_deadline)) {
        selectedPrice = event.early_bird_price;
        priceType = 'Early Bird';
      }
    }
    return { fee: parseFloat(selectedPrice) || 0, priceType };
  };

  const { fee: eventFee, priceType } = computeEventFee();
  const usdDue = eventFee + crceFee + certificateFee;
  const totalDue = usdDue + cpdAfricaFee;
  const paymentRequired = event ? (usdDue > 0 || cpdAfricaFee > 0) : false;
  const paymentMethod = cpdAfricaFee > 0 ? 'paystack' : (usdDue > 0 ? 'stripe' : null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateStep1 = () => {
    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.email = 'Enter a valid email address';
    if (!form.phone.trim()) errors.phone = 'Phone is required';
    if (!form.country.trim()) errors.country = 'Country is required';
    if (!form.organization.trim()) errors.organization = 'Organization is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!isOctoberEvent) {
      if (!form.requiresCpd) errors.requiresCpd = 'Please select an option';
      if (wantsCpd) {
        const body = form.governingBody === 'Other' ? form.governingBodyOther : form.governingBody;
        if (!body || !body.trim()) errors.governingBody = 'Professional Governing Body is required';
        if (!form.pinNumber.trim()) errors.pinNumber = 'Registration/PIN number is required';
        if (!form.areaOfStudy.trim()) errors.areaOfStudy = 'Area of study is required';
        if (!form.profession) errors.profession = 'Profession is required';
        if (form.profession === 'Other' && !form.professionOther.trim()) errors.professionOther = 'Please specify your profession';
        if (cpdTiers.length > 0 && !form.cpdPoints) errors.cpdPoints = 'Please select a CPD points option';
        if (cpdTiers.length === 0) errors.cpdPoints = 'CPD points are not available for this event. Please select "No".';
      }
    } else {
      if (wantsCpdAfrica) {
        if (!form.professionalPin.trim()) errors.professionalPin = 'Professional PIN is required for CPD Africa';
        if (form.cpdAlreadyPaid && !form.cpdMomoTransactionId.trim()) {
          errors.cpdMomoTransactionId = 'MoMo Transaction ID is required as proof of CPD payment';
        }
        if (!form.cpdAlreadyPaid && claimsArCaMember && !user && !form.membershipIdNumber.trim()) {
          errors.membershipIdNumber = 'ARCA Membership ID Number is required to claim member pricing';
        }
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const governingBody = form.governingBody === 'Other' ? form.governingBodyOther : form.governingBody;

      // Only send the auth header when a token exists (guests register without one)
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/events/${eventId}/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          country: form.country,
          organization: form.organization,
          jobTitle: form.jobTitle,
          requiresCpd: wantsCpd,
          cpdPoints: wantsCpd ? form.cpdPoints : null,
          governingBody: wantsCpd ? governingBody : null,
          pinNumber: wantsCpd ? form.pinNumber : null,
          areaOfStudy: wantsCpd ? form.areaOfStudy : null,
          profession: wantsCpd ? (form.profession === 'Other' ? form.professionOther : form.profession) : null,
          eventAmount: eventFee,
          crceSelected: form.crceSelected,
          cpdAfricaSelected: form.cpdAfricaSelected,
          certificateSelected: form.certificateSelected,
          isArCaMember: form.isArCaMember,
          membershipIdNumber: form.membershipIdNumber,
          professionalPin: form.professionalPin,
          cpdAlreadyPaid: form.cpdAlreadyPaid,
          cpdMomoTransactionId: form.cpdMomoTransactionId
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.data.paymentRequired) {
          // Proceed to payment workflow
          // Pass full payer details so CPD payers can be identified on the payment page
          navigate('/event-payment', {
            state: {
              eventId: event.id,
              eventName: event.name,
              eventDate: event.start_date,
              amount: data.data.amountDue,
              usdDue: data.data.usdDue,
              cpdAfricaFee: data.data.cpdAfricaFee,
              cpdAfricaCurrency: data.data.cpdAfricaCurrency,
              cpdAfricaMember: data.data.cpdAfricaMember,
              membershipIdNumber: data.data.membershipIdNumber,
              professionalPin: data.data.professionalPin,
              priceType: priceType,
              eventFee: data.data.eventFee,
              crceFee: data.data.crceFee,
              certificateFee: data.data.certificateFee,
              cpdFee: data.data.cpdFee,
              cpdPoints: data.data.cpdPoints,
              registrationId: data.data.registrationId,
              firstName: form.firstName,
              lastName: form.lastName,
              email: form.email,
              requiresCpd: wantsCpd || wantsCpdAfrica,
              paymentMethod: data.data.paymentMethod,
              paystackLink: data.data.paystackLink
            }
          });
        } else {
          setStep(4);
        }
      } else {
        alert(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      alert('Failed to register for the event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ['Your Details', 'CPD Points', 'Review', 'Confirmation'];

  if (loading) {
    return (
      <PublicLayout user={user}>
        <div className="container py-5">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-3 text-muted">Loading registration form...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !event) {
    return (
      <PublicLayout user={user}>
        <div className="container py-5 text-center">
          <div className="text-danger mb-3">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px' }}></i>
          </div>
          <h3 className="h4 fw-bold">{error || 'Event not found'}</h3>
          <button onClick={() => navigate('/events')} className="btn btn-success mt-3">
            <i className="fas fa-arrow-left me-2"></i>Back to Events
          </button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout user={user}>
      {/* ARCA color scheme for form fields - visible borders so users can see the input rows */}
      <style>{`
        .arca-reg-form .form-control:not(.is-invalid),
        .arca-reg-form .form-select:not(.is-invalid) {
          border: 2px solid #6B8E23;
        }
        .arca-reg-form .form-control:focus,
        .arca-reg-form .form-select:focus {
          border-color: #2C5234;
          box-shadow: 0 0 0 0.2rem rgba(107, 142, 35, 0.25);
        }
        .arca-reg-form .form-check-input:not(.is-invalid) {
          border: 2px solid #6B8E23;
        }
        .arca-reg-form .form-check-input:checked {
          background-color: #6B8E23;
          border-color: #6B8E23;
        }
        .arca-reg-form .form-label {
          color: #2C5234;
          font-weight: 600;
        }
      `}</style>
      <div className="bg-light py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Header */}
              <div className="mb-4">
                <button
                  onClick={() => navigate(`/events/${eventId}`)}
                  className="btn btn-outline-secondary btn-sm mb-3"
                >
                  <i className="fas fa-arrow-left me-2"></i>Back to Event
                </button>
                <h1 className="h3 fw-bold text-dark">Register: {event.name}</h1>
                <p className="text-muted mb-0">
                  <i className="fas fa-calendar me-2"></i>
                  {new Date(event.start_date).toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                  <span className={`badge ms-3 ${event.category === 'Paid' ? 'bg-warning text-dark' : 'bg-info text-white'}`}>
                    {event.category}
                  </span>
                </p>
              </div>

              {/* Step indicator */}
              <div className="d-flex mb-4">
                {stepLabels.map((label, i) => {
                  const num = i + 1;
                  const active = step === num;
                  const done = step > num;
                  return (
                    <div key={label} className="flex-fill text-center">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white mb-1"
                        style={{
                          width: '36px', height: '36px',
                          backgroundColor: done || active ? BRAND : '#ced4da'
                        }}
                      >
                        {done ? <i className="fas fa-check"></i> : num}
                      </div>
                      <div className={`small ${active ? 'fw-bold text-dark' : 'text-muted'}`}>{label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="card border-0 shadow-sm arca-reg-form">
                <div className="card-body p-4">
                  {/* ── STEP 1: Registration details ── */}
                  {step === 1 && (
                    <>
                      <h5 className="fw-bold mb-4">Your Details</h5>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">First Name *</label>
                          <input type="text" name="firstName" className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                            value={form.firstName} onChange={handleChange} />
                          {formErrors.firstName && <div className="invalid-feedback">{formErrors.firstName}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Last Name *</label>
                          <input type="text" name="lastName" className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`}
                            value={form.lastName} onChange={handleChange} />
                          {formErrors.lastName && <div className="invalid-feedback">{formErrors.lastName}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email *</label>
                          <input type="email" name="email" className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                            value={form.email} onChange={handleChange} />
                          {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Phone *</label>
                          <input type="tel" name="phone" className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                            value={form.phone} onChange={handleChange} />
                          {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Country *</label>
                          <input type="text" name="country" className={`form-control ${formErrors.country ? 'is-invalid' : ''}`}
                            value={form.country} onChange={handleChange} />
                          {formErrors.country && <div className="invalid-feedback">{formErrors.country}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Organization *</label>
                          <input type="text" name="organization" className={`form-control ${formErrors.organization ? 'is-invalid' : ''}`}
                            value={form.organization} onChange={handleChange} />
                          {formErrors.organization && <div className="invalid-feedback">{formErrors.organization}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Job Title <span className="text-muted">(optional)</span></label>
                          <input type="text" name="jobTitle" className="form-control"
                            value={form.jobTitle} onChange={handleChange} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── STEP 2: CPD / CRCE / Certificate options ── */}
                  {step === 2 && (
                    <>
                      <h5 className="fw-bold mb-3">Certification Options</h5>

                      {isOctoberEvent ? (
                        <div className="mb-3">
                          <p className="text-muted small mb-3">
                            Select any certification options you need for this webinar series.
                          </p>

                          {/* CRCE Points */}
                          <div className="form-check mb-3 p-3 rounded" style={{ backgroundColor: '#f8faf5', border: '1px solid #e0e8d0' }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="crceSelected"
                              id="crceSelected"
                              checked={form.crceSelected}
                              onChange={(e) => setForm(prev => ({ ...prev, crceSelected: e.target.checked }))}
                            />
                            <label className="form-check-label w-100" htmlFor="crceSelected">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>CRCE Points</strong>
                                  <div className="small text-muted">1.5 CRCE POINTS PER WEBINAR</div>
                                </div>
                                <span className="fw-bold" style={{ color: '#6B8E23' }}>$10.00</span>
                              </div>
                            </label>
                          </div>

                          {/* CPD Africa */}
                          <div className="form-check mb-3 p-3 rounded" style={{ backgroundColor: '#f8faf5', border: '1px solid #e0e8d0' }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="cpdAfricaSelected"
                              id="cpdAfricaSelected"
                              checked={form.cpdAfricaSelected}
                              onChange={(e) => setForm(prev => ({ ...prev, cpdAfricaSelected: e.target.checked, cpdAlreadyPaid: false, cpdMomoTransactionId: '' }))}
                            />
                            <label className="form-check-label w-100" htmlFor="cpdAfricaSelected">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>CPD (Africa) Points</strong>
                                  <div className="small text-muted">20 points for all webinar sessions</div>
                                </div>
                                {!cpdAfricaAlreadyPaid && (
                                  <div className="text-end">
                                    <div className="small" style={{ fontSize: '0.8rem', color: isCpdAfricaMember ? '#999' : '#333' }}>
                                      <span className={isCpdAfricaMember ? 'text-decoration-line-through' : 'fw-bold'}>GHC 200.00 (Non-Member)</span>
                                    </div>
                                    <div className="fw-bold" style={{ color: '#6B8E23', fontSize: isCpdAfricaMember ? '1rem' : '0.8rem' }}>
                                      <span className={!isCpdAfricaMember ? 'text-decoration-line-through' : ''}>GHC 150.00 (Member)</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </label>

                            {wantsCpdAfrica && (
                              <div className="mt-3 pt-3 border-top">
                                <div className="alert alert-danger small fw-bold mb-3" style={{ color: '#c53030', backgroundColor: '#fff5f5', borderColor: '#c53030' }}>
                                  <i className="fas fa-exclamation-circle me-2"></i>
                                  NOTE: CPD payment is ONCE. If you already paid during your first registration, select "Yes" below and provide your proof. Ignore CPD payment if already paid.
                                </div>

                                <div className="mb-3">
                                  <label className="form-label small fw-bold">Have you already paid for CPD Africa?</label>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="cpdAlreadyPaid"
                                      id="cpdAlreadyPaidYes"
                                      value="true"
                                      checked={form.cpdAlreadyPaid === true}
                                      onChange={() => setForm(prev => ({ ...prev, cpdAlreadyPaid: true }))}
                                    />
                                    <label className="form-check-label" htmlFor="cpdAlreadyPaidYes">Yes — I already paid during my first registration</label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="cpdAlreadyPaid"
                                      id="cpdAlreadyPaidNo"
                                      value="false"
                                      checked={form.cpdAlreadyPaid === false}
                                      onChange={() => setForm(prev => ({ ...prev, cpdAlreadyPaid: false, cpdMomoTransactionId: '' }))}
                                    />
                                    <label className="form-check-label" htmlFor="cpdAlreadyPaidNo">No — I will pay now</label>
                                  </div>
                                </div>

                                {!form.cpdAlreadyPaid && !user && (
                                  <div className="form-check mb-3">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      name="isArCaMember"
                                      id="isArCaMember"
                                      checked={form.isArCaMember}
                                      onChange={(e) => setForm(prev => ({ ...prev, isArCaMember: e.target.checked }))}
                                    />
                                    <label className="form-check-label" htmlFor="isArCaMember">
                                      <strong>I am an ARCA member</strong> (GHC 150)
                                    </label>
                                  </div>
                                )}

                                <div className="row g-2">
                                  {form.cpdAlreadyPaid && (
                                    <div className="col-12">
                                      <label className="form-label small">MoMo Transaction ID *</label>
                                      <input
                                        type="text"
                                        name="cpdMomoTransactionId"
                                        className={`form-control form-control-sm ${formErrors.cpdMomoTransactionId ? 'is-invalid' : ''}`}
                                        value={form.cpdMomoTransactionId}
                                        onChange={handleChange}
                                        placeholder="Enter your MoMo Transaction ID from first registration"
                                      />
                                      {formErrors.cpdMomoTransactionId && <div className="invalid-feedback">{formErrors.cpdMomoTransactionId}</div>}
                                    </div>
                                  )}
                                  {claimsArCaMember && !user && !form.cpdAlreadyPaid && (
                                    <div className="col-md-6">
                                      <label className="form-label small">ARCA Membership ID Number *</label>
                                      <input
                                        type="text"
                                        name="membershipIdNumber"
                                        className={`form-control form-control-sm ${formErrors.membershipIdNumber ? 'is-invalid' : ''}`}
                                        value={form.membershipIdNumber}
                                        onChange={handleChange}
                                        placeholder="e.g. ARCA-12345"
                                      />
                                      {formErrors.membershipIdNumber && <div className="invalid-feedback">{formErrors.membershipIdNumber}</div>}
                                    </div>
                                  )}
                                  <div className={(claimsArCaMember && !user && !form.cpdAlreadyPaid) ? 'col-md-6' : 'col-12'}>
                                    <label className="form-label small">Professional PIN *</label>
                                    <input
                                      type="text"
                                      name="professionalPin"
                                      className={`form-control form-control-sm ${formErrors.professionalPin ? 'is-invalid' : ''}`}
                                      value={form.professionalPin}
                                      onChange={handleChange}
                                      placeholder="Professional registration PIN"
                                    />
                                    {formErrors.professionalPin && <div className="invalid-feedback">{formErrors.professionalPin}</div>}
                                  </div>
                                </div>

                                {user && !form.cpdAlreadyPaid && (
                                  <div className="mt-2 small text-success">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Member pricing applied (logged in as ARCA member)
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Certificate */}
                          <div className="form-check mb-3 p-3 rounded" style={{ backgroundColor: '#f8faf5', border: '1px solid #e0e8d0' }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="certificateSelected"
                              id="certificateSelected"
                              checked={form.certificateSelected}
                              onChange={(e) => setForm(prev => ({ ...prev, certificateSelected: e.target.checked }))}
                            />
                            <label className="form-check-label w-100" htmlFor="certificateSelected">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>Certificate</strong>
                                  <div className="small text-muted">
                                    {wantsCrce || wantsCpdAfrica
                                      ? 'FREE — included with CRCE or CPD payment'
                                      : '$10 per topic'}
                                  </div>
                                </div>
                                <span className="fw-bold" style={{ color: '#6B8E23' }}>
                                  {wantsCrce || wantsCpdAfrica ? 'FREE' : '$10.00'}
                                </span>
                              </div>
                            </label>
                          </div>

                          {(wantsCrce || wantsCpdAfrica || wantsCertificate) && (
                            <div className="alert alert-info small mt-3">
                              <i className="fas fa-info-circle me-2"></i>
                              {wantsCpdAfrica && !form.cpdAlreadyPaid
                                ? 'CPD Africa payment will be processed in GHC. Certificate is included free.'
                                : wantsCpdAfrica && form.cpdAlreadyPaid
                                  ? 'CPD already paid. Certificate is included free.'
                                  : wantsCrce
                                    ? 'CRCE payment will be processed to complete your registration. Certificate is included free.'
                                    : 'Certificate payment will be processed to complete your registration.'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="alert alert-info small">
                            <i className="fas fa-certificate me-2"></i>
                            CPD Points come with an <strong>official certificate</strong> issued after the event.
                          </div>
                          <label className="form-label fw-bold">Do you require CPD Points? *</label>
                          <div className="mb-3">
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="requiresCpd" id="cpdYes"
                                value="yes" checked={form.requiresCpd === 'yes'} onChange={handleChange} />
                              <label className="form-check-label" htmlFor="cpdYes">Yes, I require CPD Points (with Certificate)</label>
                            </div>
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="requiresCpd" id="cpdNo"
                                value="no" checked={form.requiresCpd === 'no'} onChange={handleChange} />
                              <label className="form-check-label" htmlFor="cpdNo">No</label>
                            </div>
                            {formErrors.requiresCpd && <div className="text-danger small mt-1">{formErrors.requiresCpd}</div>}
                          </div>

                          {wantsCpd && (
                            <div className="border rounded p-3" style={{ backgroundColor: '#f8faf5' }}>
                              <div className="alert alert-warning small">
                                <i className="fas fa-info-circle me-2"></i>
                                CPD certification (with certificate) requires payment{event.category === 'Free' ? ', even though this event is free' : ' in addition to the event fee'}.
                              </div>

                              <div className="mb-3">
                                <label className="form-label">CPD Points *</label>
                                {cpdTiers.length > 0 ? (
                                  <>
                                    <select name="cpdPoints" className={`form-select ${formErrors.cpdPoints ? 'is-invalid' : ''}`}
                                      value={form.cpdPoints} onChange={handleChange}>
                                      <option value="">Select CPD points...</option>
                                      {cpdTiers.map(tier => (
                                        <option key={tier.points} value={tier.points}>
                                          {tier.points} Points — ${parseFloat(tier.price).toFixed(2)}
                                        </option>
                                      ))}
                                    </select>
                                    {formErrors.cpdPoints && <div className="invalid-feedback">{formErrors.cpdPoints}</div>}
                                  </>
                                ) : (
                                  <div className="alert alert-danger small mb-0">
                                    CPD points are not available for this event.
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Professional Governing Body *</label>
                                <input
                                  type="text"
                                  name="governingBody"
                                  className={`form-control ${formErrors.governingBody ? 'is-invalid' : ''}`}
                                  value={form.governingBody}
                                  onChange={handleChange}
                                  list="governing-bodies"
                                  placeholder="Start typing to see suggestions..."
                                />
                                <datalist id="governing-bodies">
                                  {GOVERNING_BODIES.map(body => <option key={body} value={body} />)}
                                  <option value="Other" />
                                </datalist>
                                {formErrors.governingBody && <div className="invalid-feedback">{formErrors.governingBody}</div>}
                                <small className="text-muted">Type your governing body or select from suggestions. Enter "Other" to specify a custom one.</small>
                              </div>

                              {form.governingBody === 'Other' && (
                                <div className="mb-3">
                                  <label className="form-label">Specify Governing Body *</label>
                                  <input type="text" name="governingBodyOther" className="form-control"
                                    value={form.governingBodyOther} onChange={handleChange} />
                                </div>
                              )}

                              <div className="mb-3">
                                <label className="form-label">Professional Registration/PIN Number *</label>
                                <input type="text" name="pinNumber" className={`form-control ${formErrors.pinNumber ? 'is-invalid' : ''}`}
                                  value={form.pinNumber} onChange={handleChange} />
                                {formErrors.pinNumber && <div className="invalid-feedback">{formErrors.pinNumber}</div>}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Area of Study *</label>
                                <input type="text" name="areaOfStudy" className={`form-control ${formErrors.areaOfStudy ? 'is-invalid' : ''}`}
                                  value={form.areaOfStudy} onChange={handleChange} />
                                {formErrors.areaOfStudy && <div className="invalid-feedback">{formErrors.areaOfStudy}</div>}
                              </div>

                              <div className="mb-2">
                                <label className="form-label">Profession *</label>
                                <select name="profession" className={`form-select ${formErrors.profession ? 'is-invalid' : ''}`}
                                  value={form.profession} onChange={handleChange}>
                                  <option value="">Select your profession...</option>
                                  {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                                {formErrors.profession && <div className="invalid-feedback">{formErrors.profession}</div>}
                              </div>

                              {form.profession === 'Other' && (
                                <div className="mb-2">
                                  <label className="form-label">Specify Profession *</label>
                                  <input
                                    type="text"
                                    name="professionOther"
                                    className={`form-control ${formErrors.professionOther ? 'is-invalid' : ''}`}
                                    value={form.professionOther}
                                    onChange={handleChange}
                                    placeholder="Enter your profession"
                                  />
                                  {formErrors.professionOther && <div className="invalid-feedback">{formErrors.professionOther}</div>}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* ── STEP 3: Review ── */}
                  {step === 3 && (
                    <>
                      <h5 className="fw-bold mb-4">Review Your Registration</h5>

                      <h6 className="fw-bold text-muted small text-uppercase">Your Details</h6>
                      <div className="row small mb-3">
                        <div className="col-6 mb-1"><span className="text-muted">Name:</span> {form.firstName} {form.lastName}</div>
                        <div className="col-6 mb-1"><span className="text-muted">Email:</span> {form.email}</div>
                        <div className="col-6 mb-1"><span className="text-muted">Phone:</span> {form.phone}</div>
                        <div className="col-6 mb-1"><span className="text-muted">Country:</span> {form.country}</div>
                        <div className="col-6 mb-1"><span className="text-muted">Organization:</span> {form.organization}</div>
                        {form.jobTitle && <div className="col-6 mb-1"><span className="text-muted">Job Title:</span> {form.jobTitle}</div>}
                      </div>

                      <h6 className="fw-bold text-muted small text-uppercase">Certification Options</h6>
                      {isOctoberEvent ? (
                        <div className="row small mb-3">
                          <div className="col-6 mb-1">
                            <span className="text-muted">CRCE Points:</span>{' '}
                            {wantsCrce ? 'Selected (1.5 CRCE points per webinar)' : 'Not selected'}
                          </div>
                          <div className="col-6 mb-1">
                            <span className="text-muted">CPD Africa Points:</span>{' '}
                            {wantsCpdAfrica
                              ? `Selected (20 points) — ${form.cpdAlreadyPaid ? 'Already paid' : (isCpdAfricaMember ? 'Member' : 'Non-Member')}`
                              : 'Not selected'}
                          </div>
                          {wantsCpdAfrica && (
                            <>
                              <div className="col-6 mb-1"><span className="text-muted">Professional PIN:</span> {form.professionalPin}</div>
                              {form.cpdAlreadyPaid && (
                                <div className="col-6 mb-1"><span className="text-muted">MoMo Transaction ID:</span> {form.cpdMomoTransactionId}</div>
                              )}
                              {!form.cpdAlreadyPaid && claimsArCaMember && !user && (
                                <div className="col-6 mb-1"><span className="text-muted">Membership ID:</span> {form.membershipIdNumber}</div>
                              )}
                            </>
                          )}
                          <div className="col-6 mb-1">
                            <span className="text-muted">Certificate:</span>{' '}
                            {wantsCertificate ? 'Selected' : 'Not selected'}
                          </div>
                        </div>
                      ) : (
                        wantsCpd ? (
                          <div className="row small mb-3">
                            <div className="col-6 mb-1"><span className="text-muted">CPD Points:</span> {form.cpdPoints} Points</div>
                            <div className="col-6 mb-1"><span className="text-muted">Governing Body:</span> {form.governingBody === 'Other' ? form.governingBodyOther : form.governingBody}</div>
                            <div className="col-6 mb-1"><span className="text-muted">PIN Number:</span> {form.pinNumber}</div>
                            <div className="col-6 mb-1"><span className="text-muted">Area of Study:</span> {form.areaOfStudy}</div>
                            <div className="col-6 mb-1"><span className="text-muted">Profession:</span> {form.profession === 'Other' ? form.professionOther : form.profession}</div>
                          </div>
                        ) : (
                          <p className="small text-muted mb-3">Not required</p>
                        )
                      )}

                      <hr />
                      <h6 className="fw-bold text-muted small text-uppercase">Fees</h6>
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">Event Fee{event.category === 'Paid' ? ` (${priceType})` : ''}:</span>
                        <span>{eventFee > 0 ? `$${eventFee.toFixed(2)}` : 'Free'}</span>
                      </div>
                      {!isOctoberEvent && wantsCpd && (
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">CPD Fee ({form.cpdPoints} Points + Certificate):</span>
                          <span>${cpdFee.toFixed(2)}</span>
                        </div>
                      )}
                      {isOctoberEvent && wantsCrce > 0 && (
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">CRCE Points (1.5 points per webinar):</span>
                          <span>${crceFee.toFixed(2)}</span>
                        </div>
                      )}
                      {isOctoberEvent && wantsCpdAfrica > 0 && (
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">CPD Africa Points (20 points):</span>
                          <span>{form.cpdAlreadyPaid ? 'Already paid' : `GHC ${cpdAfricaFee.toFixed(2)}`}</span>
                        </div>
                      )}
                      {isOctoberEvent && wantsCertificate && (
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Certificate:</span>
                          <span>{certificateFee > 0 ? `$${certificateFee.toFixed(2)}` : 'Free'}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between fw-bold mt-2">
                        <span>Total:</span>
                        <span style={{ color: BRAND }}>
                          {wantsCpdAfrica && !form.cpdAlreadyPaid
                            ? `GHC ${cpdAfricaFee.toFixed(2)}${usdDue > 0 ? ` + $${usdDue.toFixed(2)}` : ''}`
                            : (totalDue > 0 ? `$${totalDue.toFixed(2)}` : 'Free')}
                        </span>
                      </div>

                      {paymentRequired && (
                        <div className="alert alert-info small mt-3 mb-0">
                          <i className="fas fa-credit-card me-2"></i>
                          {wantsCpdAfrica && !form.cpdAlreadyPaid
                            ? 'You will be redirected to complete your CPD Africa payment in GHC.'
                            : 'You will be taken to the secure payment page to complete your registration.'}
                        </div>
                      )}
                    </>
                  )}

                  {/* ── STEP 4: Confirmation (free, no CPD) ── */}
                  {step === 4 && (
                    <div className="text-center py-4">
                      <div className="mb-3" style={{ color: BRAND }}>
                        <i className="fas fa-check-circle" style={{ fontSize: '64px' }}></i>
                      </div>
                      <h4 className="fw-bold">Registration Confirmed!</h4>
                      <p className="text-muted">
                        You are registered for <strong>{event.name}</strong>.<br />
                        A confirmation email has been sent to <strong>{form.email}</strong>.
                      </p>
                      <button className="btn text-white fw-bold mt-2" style={{ backgroundColor: BRAND }}
                        onClick={() => navigate('/events')}>
                        Back to Events
                      </button>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  {step < 4 && (
                    <div className="d-flex justify-content-between mt-4">
                      {step > 1 ? (
                        <button className="btn btn-outline-secondary" onClick={() => setStep(step - 1)} disabled={submitting}>
                          <i className="fas fa-arrow-left me-2"></i>Back
                        </button>
                      ) : <span />}

                      {step < 3 ? (
                        <button className="btn text-white fw-bold" style={{ backgroundColor: BRAND }} onClick={goNext}>
                          Continue<i className="fas fa-arrow-right ms-2"></i>
                        </button>
                      ) : (
                        <button className="btn text-white fw-bold" style={{ backgroundColor: BRAND }}
                          onClick={handleSubmit} disabled={submitting}>
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Submitting...
                            </>
                          ) : paymentRequired ? (
                            <>Confirm & Proceed to Payment<i className="fas fa-lock ms-2"></i></>
                          ) : (
                            <>Complete Registration<i className="fas fa-check ms-2"></i></>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default EventRegistrationPage;
